import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { types } from '@swyftx/marketing-utils';

import { ErrorMessageBox } from '@global-components/message-boxes/ErrorMessageBox';
import { SuccessMessageBox } from '@global-components/message-boxes/SuccessMessageBox';

import { UIStore, UserStore } from '@shared/store';

import { RewardsContext } from '@routes/Rewards/Rewards.context';

import { observer } from 'mobx-react-lite';

import Content from './Content';
import Header from './Header';
import { RewardCardCountdown } from './RewardCardCountdown';
import { RewardCardRedeemStatus } from './RewardCardRedeemStatus';
import { useReward } from '../../hooks/useReward.hooks';

type Props = {
  reward: types.UserAssetReward;
};

const RewardCard: React.FC<Props> = observer(({ reward }) => {
  const { buttonLabel, buttonDisabled } = useReward(reward);
  const { addMessageBox } = UIStore.useUIStore;
  const { userProfile } = UserStore.useUserStore;
  const { t } = useTranslation('rewards', { keyPrefix: 'rewardCard' });
  const { optIn, redeemReward } = useContext(RewardsContext);

  const onAction = () => {
    if (!reward.redemptions.length) {
      if (userProfile?.intercom?.uuid) {
        optIn(reward.id, userProfile.intercom.uuid);
        addMessageBox({
          content: (
            <SuccessMessageBox title={t('subscribeMessage', { campaignName: (reward as any).campaignName || '' })} />
          ),
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      } else {
        addMessageBox({
          content: (
            <ErrorMessageBox title={t('subscribeFailMessage', { campaignName: (reward as any).campaignName || '' })} />
          ),
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      }
    } else if (userProfile?.intercom?.uuid) {
      redeemReward(reward, userProfile.intercom.uuid);
    }
  };

  const redemption = useMemo(() => {
    if (!reward.redemptions.length) return undefined;
    return reward.redemptions[0];
  }, [reward]);

  const showButton = useMemo(() => {
    const showButtonTypes: types.AssetRedemptionStatus[] = [
      types.AssetRedemptionStatus.InProgress,
      types.AssetRedemptionStatus.Qualified,
    ];
    if (!redemption && reward.status === types.AssetRewardStatus.Live) return true;
    if (redemption && redemption.status && showButtonTypes.includes(redemption?.status)) return true;

    return false;
  }, [redemption, reward]);

  const showCountdown = useMemo(() => {
    const showCountdownTypes: types.AssetRedemptionStatus[] = [
      types.AssetRedemptionStatus.InProgress,
      types.AssetRedemptionStatus.Qualified,
    ];
    if (!redemption && reward.status === types.AssetRewardStatus.Live) return true;
    if (redemption && showCountdownTypes.includes(redemption.status)) return true;

    return false;
  }, [reward, redemption]);

  return (
    <Card className='grid gap-16 p-24'>
      <Header reward={reward} />
      {showCountdown && <RewardCardCountdown reward={reward} />}

      <Content reward={reward} redemption={redemption}>
        {showButton && (
          <Button
            className='mt-4 hidden max-w-[150px] self-end md:block'
            onClick={onAction}
            color='primary'
            variant='filled'
            disabled={buttonDisabled}
            size='lg'
          >
            {buttonLabel}
          </Button>
        )}
      </Content>

      {(redemption || (reward.endDate && reward.endDate.getTime() < new Date().getTime())) && (
        <RewardCardRedeemStatus redemption={redemption} />
      )}

      {showButton && (
        <Button
          className='mt-4 block self-end md:hidden'
          onClick={onAction}
          color='primary'
          variant='filled'
          disabled={buttonDisabled}
          size='lg'
        >
          {buttonLabel}
        </Button>
      )}
    </Card>
  );
});

export { RewardCard };
